import React, { useState, useMemo, useEffect } from 'react';
import { useMutation } from '@apollo/client';
import _ from 'lodash';
import PropTypes from 'prop-types';

import {
  ButtonMerge,
  ButtonUnmerge,
  Container,
  ContainerButtons,
  ContainerHeader,
} from './styles';

import Table from './table-categories';
import ModalMerge from './modal-merge';
import ModalUnmerge from './modal-unmerge';

import {
  MERGE_CATEGORIES,
  UNMERGE_CATEGORIES,
  GET_ANALYTICS_CARD_SORT,
} from '../../graphql';

const TYPENAME = '__typename';

const CategoriesComponent = (props) => {
  const {
    project,
    analyticsCardSort,
  } = props;

  const [selectedCategories, setSelectedCategories] = useState([]);
  const [isMergeModalOpen, setIsMergeModalOpen] = useState(false);
  const [isUnmergeModalOpen, setIsUnmergeModalOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const handleOpenMergeModal = () => setIsMergeModalOpen(true);
  const handleCloseMergeModal = () => setIsMergeModalOpen(false);
  const handleOpenUnmergeModal = () => setIsUnmergeModalOpen(true);
  const handleCloseUnmergeModal = () => setIsUnmergeModalOpen(false);

  const [createMergedCategory, {
    loading: loadingMerge,
  }] = useMutation(MERGE_CATEGORIES, {
    refetchQueries: [{
      query: GET_ANALYTICS_CARD_SORT,
      variables: {
        projectId: project.id,
      },
    }],
    awaitRefetchQueries: true,
  });

  const [deleteMergedCategory, {
    loading: loadingUnmerge,
  }] = useMutation(UNMERGE_CATEGORIES, {
    refetchQueries: [{
      query: GET_ANALYTICS_CARD_SORT,
      variables: {
        projectId: project.id,
      },
    }],
    awaitRefetchQueries: true,
  });

  useEffect(() => {
    const loading = loadingMerge || loadingUnmerge;
    setIsLoading(loading);
  }, [loadingMerge, loadingUnmerge, setIsLoading]);

  const isClosedType = project.cardSortType === 'closed';

  const groupedAnalyticsByCategoryId = useMemo(() => _.groupBy(analyticsCardSort, (item) => item.cardSortCategory.id), [analyticsCardSort]);

  const allCategories = useMemo(() => _.map(groupedAnalyticsByCategoryId,
    (group) => _.head(group).cardSortCategory),
  [groupedAnalyticsByCategoryId]);

  const handleSelectOrUnselectAll = (e, { checked }) => {
    if (checked) {
      setSelectedCategories(allCategories);
      return;
    }
    setSelectedCategories([]);
  };

  const handleSelectOrUnselectItem = (category, { checked }) => {
    if (!checked) {
      const newArray = _.filter(selectedCategories, (x) => x.id !== category.id);
      setSelectedCategories(newArray);
      return;
    }
    setSelectedCategories([...selectedCategories, category]);
  };

  const isAllChecked = useMemo(() => (selectedCategories.length === allCategories.length),
    [allCategories.length, selectedCategories.length]);

  const selectedMergedCategories = useMemo(() => _.filter(
    selectedCategories, (category) => category[TYPENAME] === 'CardSortMergedCategory',
  ), [selectedCategories]);

  return (
    <Container>
      <ContainerHeader>
        {!isClosedType && (
          <ContainerButtons>

            <ButtonUnmerge
              disabled={selectedMergedCategories.length === 0}
              notAllowed={selectedMergedCategories.length === 0}
              onClick={handleOpenUnmergeModal}
            >
              Unmerge Categories
            </ButtonUnmerge>

            <ButtonMerge
              disabled={selectedCategories.length < 2}
              notAllowed={selectedCategories.length < 2}
              onClick={handleOpenMergeModal}
            >
              Merge Categories
            </ButtonMerge>

          </ContainerButtons>
        )}
      </ContainerHeader>

      <Table
        isLoading={isLoading}
        isClosedType={isClosedType}
        isAllChecked={isAllChecked}
        onSelectOrUnselectAll={handleSelectOrUnselectAll}
        onSelectOrUnselectItem={handleSelectOrUnselectItem}
        selectedCategories={selectedCategories}
        groupedAnalyticsByCategoryId={groupedAnalyticsByCategoryId}
      />

      {isUnmergeModalOpen && (
        <ModalUnmerge
          onClose={handleCloseUnmergeModal}
          selectedCategories={selectedCategories}
          projectId={project.id}
          onClearCategories={() => setSelectedCategories([])}
          deleteMergedCategory={deleteMergedCategory}
        />
      )}

      {isMergeModalOpen && (
        <ModalMerge
          onClose={handleCloseMergeModal}
          selectedCategories={selectedCategories}
          projectId={project.id}
          onClearCategories={() => setSelectedCategories([])}
          createMergedCategory={createMergedCategory}
        />
      )}
    </Container>
  );
};

CategoriesComponent.propTypes = {
  analyticsCardSort: PropTypes.arrayOf(PropTypes.shape({
    cardSortCategories: PropTypes.object,
    cardSortItems: PropTypes.object,
    frequencyPercentage: PropTypes.number,
    frequencyTotal: PropTypes.number,
  })).isRequired,
  project: PropTypes.shape({
    cardSortType: PropTypes.string,
    id: PropTypes.string,
  }).isRequired,
};

export default CategoriesComponent;
